import { useRef, useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { getApiErrorMessage } from '@shared/utils/apiError.utils';
import { useWorkspaceScope } from './useWorkspaceScope';
import {
  useCheckWorkspaceMissionConfigurationMutation,
  type MissionConfigurationCheck,
  type MissionSpec,
} from './workspaceApi';

export const MissionConfigurationChecks = ({ spec }: { spec: MissionSpec }) => {
  const scope = useWorkspaceScope();
  const fingerprint = JSON.stringify([scope, spec]);
  const [open, setOpen] = useState(false);
  const [result, setResult] = useState<{
    fingerprint: string;
    data?: MissionConfigurationCheck;
    error?: string;
  } | null>(null);
  const [check, { isLoading }] = useCheckWorkspaceMissionConfigurationMutation();
  const pending = useRef(false);
  const inspect = async () => {
    if (pending.current) return;
    pending.current = true;
    setOpen(true);
    setResult(null);
    try {
      const data = await check({ spec }).unwrap();
      setResult({ fingerprint, data });
    } catch (error) {
      setResult({
        fingerprint,
        error: getApiErrorMessage(error, 'Unable to check this draft configuration.'),
      });
    } finally {
      pending.current = false;
    }
  };
  const current = result?.fingerprint === fingerprint ? result : null;
  return (
    <>
      <Button disabled={isLoading} onClick={() => void inspect()}>
        Check configuration
      </Button>
      {open && (
        <Dialog
          open
          fullWidth
          maxWidth="md"
          onClose={() => setOpen(false)}
          aria-labelledby="mission-configuration-title">
          <DialogTitle id="mission-configuration-title">Mission configuration checks</DialogTitle>
          <DialogContent>
            <Stack gap={2}>
              <Alert severity="info">
                Read only: checks the current draft, including unsaved edits. This does not save,
                publish, create a run, call a model, start a campaign or deliver a webhook. Provider
                health, credits and runtime admission are checked at execution.
              </Alert>
              {isLoading && <CircularProgress aria-label="Checking mission configuration" />}
              {!isLoading && !current && (
                <Alert severity="warning">
                  The draft or account changed. Check this configuration again.
                </Alert>
              )}
              {current?.error && <Alert severity="error">{current.error}</Alert>}
              {current?.data && (
                <>
                  <Typography>
                    Draft format and trigger checked.
                    {current.data.nextOccurrence
                      ? ` Next scheduled occurrence: ${new Date(current.data.nextOccurrence).toLocaleString()}.`
                      : ''}
                  </Typography>
                  {current.data.actions.map((action, index) => (
                    <Alert
                      key={index}
                      severity={
                        action.status === 'blocked'
                          ? 'warning'
                          : action.status === 'deferred'
                            ? 'info'
                            : 'success'
                      }>
                      Action {index + 1} · {action.kind}: {action.message}
                    </Alert>
                  ))}
                  <Typography variant="caption" color="text.secondary">
                    Results describe the current saved targets. Permissions, target state and call
                    admission are checked again when an action executes.
                  </Typography>
                </>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button disabled={isLoading} onClick={() => void inspect()}>
              Check again
            </Button>
            <Button onClick={() => setOpen(false)}>Close checks</Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};
